/**
 * What the microdose dose step API sends the clinic card (GET /api/v1/microdose/patients/:id), and the small
 * formatting rules the card and its dialog share. Mirrors services/microdose.service.ts. Every threshold stays on the
 * server: the card is sent the measurability rule and the syringe size it draws.
 */

/* ------------------------------------------------------------------ *
 * What the API sends (GET /microdose/patients/:id)
 * ------------------------------------------------------------------ */

export interface StepView {
  step: number;
  mg: number;
  text: string;
  label: string;
}

export interface DoseView {
  mg: number;
  text: string;
  concentrationMgPerMl: number;
  mL: number;
  units: number;
  measurable: boolean;
  /** The U-100 barrel the syringe picture draws (the server chooses it). */
  scaleUnits: number;
}

/** Why a button is not available, and the rule it comes from (shown on hover, never in the layout). */
export interface Blocker {
  text: string;
  rule: string | null;
}

export interface ActionView {
  allowed: boolean;
  toMg: number | null;
  toStep: number | null;
  toText: string | null;
  dose: DoseView | null;
  blockers: Blocker[];
}

export interface DraftLadder {
  planId: string;
  planVersion: number;
  planStatus: string;
  itemName: string;
  drugName: string;
  saved: boolean;
  includeCeiling: boolean;
  steps: StepView[];
  ceiling: StepView;
  minWeeksAtStep: number;
  start: StepView;
  patientSentence: string;
  /** Why this viewer cannot change the ladder now (the plan waits in someone else's Sign queue), or null. */
  lockedReason: string | null;
}

export interface CheckRow {
  check: number;
  label: string;
  rule: string;
  pass: boolean;
  reasons: Array<{ rule: string; why: string }>;
  /** Something to know about a check that passed (check 6: the stay question was not on this check-in). */
  note: string | null;
}

export interface SignedLadder {
  id: string;
  planId: string;
  planVersion: number;
  planStatus: string;
  active: boolean;
  signedAt: string | null;
  signedBy: string | null;
  acceptedAt: string | null;
  itemName: string;
  drugName: string;
  steps: StepView[];
  ceiling: StepView;
  includeCeiling: boolean;
  minWeeksAtStep: number;
  patientSentence: string;
  measurability: { minUnits: number; unitMark: number; unitsPerMl: number };
  current: { mg: number; text: string; step: number | null; label: string | null; since: string; weeks: number; days: number; dose: DoseView | null };
  dispense: { concentrationMgPerMl: number; dispensedAt: string; note: string | null; recordedBy: string | null } | null;
  checkin: {
    formResponseId: string;
    month: number | null;
    submittedAt: string;
    ageDays: number;
    scores: Array<{ key: string; label: string; rule: string; value: number | null; baseline: number | null; change: number | null }> | null;
    sideEffects: Array<{ ref: string; label: string; answer: string | null }>;
    dosing: Array<{ ref: string; label: string; answer: string | null }>;
    detail: string | null;
  } | null;
  readiness: { ready: boolean; checks: CheckRow[]; evaluatedAt: string } | null;
  options: {
    status: 'not_active' | 'pending' | 'no_checkin' | 'ready' | 'ceiling_available' | 'stay';
    approve: ActionView & { ceiling: boolean; needsReason: boolean };
    stay: { allowed: boolean; withdraws: boolean; blockers: Blocker[] };
    stepDown: ActionView & { suggested: boolean; suggestedBy: Array<{ rule: string; why: string }> };
  };
  pending: {
    eventId: string;
    kind: 'approved' | 'step_down';
    toMg: number;
    toText: string;
    since: string;
    overdue: boolean;
    reminderAt: string | null;
    by: string | null;
    /** At the vial in use: what the patient reads and confirms. */
    dose: DoseView | null;
    /** At the vial on file when the provider decided. */
    decidedDose: DoseView | null;
    vialChanged: boolean;
  } | null;
  /** A new vial waiting for the patient to confirm the units. */
  vial: { eventId: string; since: string; overdue: boolean; reminderAt: string | null; dose: DoseView | null } | null;
  decisionForLatest: { kind: string; at: string; by: string | null; reason: string | null } | null;
  alerts: Array<{ id: string; ruleId: string; level: 'RED' | 'ORANGE' | 'YELLOW'; text: string; createdAt: string; checkinMonth: number | null }>;
  history: Array<{
    id: string;
    kind: string;
    /** For an acknowledgment or a reminder: what it answers (approved, step_down or vial_change). */
    refKind: string | null;
    at: string;
    fromText: string | null;
    toText: string | null;
    units: number | null;
    volumeMl: number | null;
    concentrationMgPerMl: number | null;
    by: string | null;
    reason: string | null;
    ruleIds: string[];
    signatureMethod: string | null;
    effectiveFrom: string | null;
  }>;
}

export interface Viewer {
  /** May set the ladder, record a vial and resolve an alert (a clinician). */
  canEdit: boolean;
  /** May Approve, Stay or Step down (a prescriber licensed in the patient's state). */
  canDecide: boolean;
  /** Why not, in the clinician's words. */
  decideBlocker: string | null;
  pinSet: boolean;
  pinLockedUntil?: string | null;
  passkeys: number;
}

export interface CardData {
  patient: { id: string; firstName: string };
  draft: DraftLadder | null;
  ladder: SignedLadder | null;
  viewer: Viewer;
}

/** A number without float noise or trailing zeros: 0.15, 7.5, 5. */
export const plain = (n: number) => String(Math.round(n * 1000) / 1000);

/** "1 unit", "1.5 units", "15 units". */
export const unitCount = (n: number) => `${plain(n)} ${plain(n) === '1' ? 'unit' : 'units'}`;

/** "15 units · 0.15 mL at 5 mg/mL": units only ever beside their concentration (OPT-STEP-006). */
export function doseLine(d: DoseView): string {
  return `${plain(d.units)} units · ${plain(d.mL)} mL at ${plain(d.concentrationMgPerMl)} mg/mL`;
}

/** The blockers as one sentence for a button's title; the rules beside them. */
export function blockerTitle(blockers: Blocker[]): string | undefined {
  if (blockers.length === 0) return undefined;
  const rules = [...new Set(blockers.map((b) => b.rule).filter((r): r is string => Boolean(r)))];
  return `${blockers.map((b) => b.text).join(' ')}${rules.length ? ` (${rules.join(', ')})` : ''}`;
}

/** A stored reason without the rule IDs older decisions wrote into the sentence ("... (OPT-HE-001)."). */
export function plainReason(reason: string): string {
  return reason.replace(/\s*\((?:OPT|SAFE)-[A-Z]+-\d+(?:[,;]\s*(?:OPT|SAFE)-[A-Z]+-\d+)*\)/g, '').replace(/\s+([.;:,])/g, '$1');
}

/** OPT-STEP-006, as the server applies it (the numbers come from the server). */
export function unitsAt(mg: number, concentration: number, rule: SignedLadder['measurability']) {
  const units = Math.round((mg / concentration) * rule.unitsPerMl * 1e6) / 1e6;
  const marks = units / rule.unitMark;
  return { units, mL: Math.round((mg / concentration) * 1e6) / 1e6, measurable: units >= rule.minUnits && Math.abs(Math.round(marks) - marks) < 1e-6 };
}
